import React from "react";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { makeStyles } from "@material-ui/core/styles";
import { useDot } from "../../context/automatonContext";
import { useUser } from "../../context/userContext";
import { MACHINE_TYPES } from "../../variables/machineVariables";
import exerciseConstants from "constants/Exercises/exerciseConstants";

/**
 * @name useStyles
 * @description Define os estilos CSS para o componente AutomatoSelector usando a API de estilos do Material-UI.
 * @returns {object} Um objeto com classes de estilo.
 */
const useStyles = makeStyles(() => ({
  /**
   * @description Estilos para o contêiner do grupo de botões.
   * Define o layout como flexbox e a orientação como horizontal para alinhar os botões lado a lado.
   */
  toggleContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    marginBottom: "2px",
  },
  /**
   * @description Estilos para cada botão de alternância no grupo.
   */
  toggleButton: {
    textTransform: "none", // Mantém o texto em minúsculas
    color: "#000",
    border: "1px solid #ccc",
    "&:hover": {
      backgroundColor: "#f5f5f5",
    },
    fontSize: "0.8rem !important",

    /**
     * @description Estilos para o botão quando ele está selecionado.
     */
    "&.Mui-selected": {
      backgroundColor: "blue !important",
      color: "#fff !important",
      "&:hover": {
        backgroundColor: "#0000CD !important",
      },
    },
    // Remove a borda esquerda do segundo botão em diante para criar um visual de grupo
    "&:not(:first-child)": {
      marginLeft: "-1px",
    },
  },
}));

/**
 * @name AutomatoSelector
 * @description Componente de seleção de tipo de autômato (AF, AP, ALL, MT).
 * Permite ao usuário escolher o tipo de autômato a ser desenhado ou manipulado.
 * @returns {JSX.Element} O componente renderizado.
 */
const AutomatoSelector = () => {
  const classes = useStyles();
  const { automatonType, setAutomatonType } = useDot();
  const { language } = useUser();

  /**
   * @name handleTypeChange
   * @description Manipula a mudança de tipo de autômato selecionado.
   * Atualiza o estado apenas se um novo valor for selecionado.
   * @param {object} event O evento de clique.
   * @param {string} newType O novo valor do tipo de autômato.
   * @returns {void}
   */
  const handleTypeChange = (event, newType) => {
    if (newType !== null) {
      setAutomatonType(newType);
    }
  };

  return (
    <ToggleButtonGroup
      value={automatonType}
      exclusive
      onChange={handleTypeChange}
      aria-label="Tipo de Autômato"
      orientation="horizontal"
      className={classes.toggleContainer}
    >
      <ToggleButton value={MACHINE_TYPES.AF} className={classes.toggleButton}>
        {exerciseConstants.machineTypesShort.AF[language]}
      </ToggleButton>
      <ToggleButton value={MACHINE_TYPES.AP} className={classes.toggleButton}>
        {exerciseConstants.machineTypesShort.AP[language]}
      </ToggleButton>
      <ToggleButton value={MACHINE_TYPES.ALL} className={classes.toggleButton}>
        {exerciseConstants.machineTypesShort.ALL[language]}
      </ToggleButton>
      <ToggleButton value={MACHINE_TYPES.MT} className={classes.toggleButton}>
        {exerciseConstants.machineTypesShort.MT[language]}
      </ToggleButton>
    </ToggleButtonGroup>
  );
};

export default AutomatoSelector;
